import { api } from "@breakout/backend/convex/_generated/api"
import { prepareClaim } from "@breakout/sdk"
import { useWallets } from "@privy-io/react-auth"
import { useMutation } from "@tanstack/react-query"
import { useAction } from "convex/react"
import { useCallback, useState } from "react"
import type { Address } from "viem"
import { getAddress, isAddress } from "viem"
import { useBreakoutConfig } from "../../lib/providers"
import { captureTradeError } from "../../lib/sentry"
import { serializeClaimPreparation } from "../../lib/tradeUtils"
import { createWalletClientFromWallet, findEmbeddedWallet } from "../../lib/wallet"
import { useInvalidateAfterTransaction } from "./useInvalidateAfterTransaction"

type ClaimStatus = "idle" | "processing" | "confirmed" | "error"

interface ClaimResult {
	success: boolean
	transactionHash: string
	amountClaimed: number
	blockNumber?: number
}

const DEFAULT_DEADLINE_SECONDS = 300

export function useClaim() {
	const config = useBreakoutConfig()
	const { wallets } = useWallets()
	const [status, setStatus] = useState<ClaimStatus>("idle")
	const executeGaslessClaim = useAction(api.domains.trading.actions.executeGaslessClaim)
	const { invalidateAfterClaim } = useInvalidateAfterTransaction()

	const mutation = useMutation({
		mutationFn: async ({ marketAddress }: { marketAddress: Address }): Promise<ClaimResult> => {
			const wallet = findEmbeddedWallet(wallets)
			if (!wallet) {
				throw new Error("No wallet connected")
			}

			const walletClient = await createWalletClientFromWallet({ wallet })
			if (!isAddress(wallet.address)) {
				throw new Error("Invalid wallet address")
			}
			const userAddress = getAddress(wallet.address)

			// 1. Prepare the claim
			setStatus("processing")
			const preparation = await prepareClaim(config.publicClient, {
				market: marketAddress,
				user: userAddress,
				chainId: config.chainId,
				deadlineSeconds: DEFAULT_DEADLINE_SECONDS,
			})

			// 2. Sign native claimWithSignature payload (silently with embedded wallet)
			const signature = await walletClient.signTypedData({
				account: userAddress,
				domain: preparation.signingPayload.domain,
				types: preparation.signingPayload.types,
				primaryType: preparation.signingPayload.primaryType,
				message: preparation.signingPayload.message,
			})

			// 3. Execute claim via gasless relayer
			const result = await executeGaslessClaim({
				preparation: serializeClaimPreparation(preparation),
				signature,
			})

			setStatus("confirmed")
			return {
				success: true,
				transactionHash: result.txHash,
				amountClaimed: preparation.claimable,
				blockNumber: result.blockNumber,
			}
		},
		onSuccess: (_, variables) => {
			const wallet = findEmbeddedWallet(wallets)
			const walletAddr =
				wallet?.address && isAddress(wallet.address) ? getAddress(wallet.address) : undefined
			if (walletAddr) {
				invalidateAfterClaim({
					marketAddress: variables.marketAddress,
					userAddress: walletAddr,
				})
			}
		},
		onError: (error, variables) => {
			setStatus("error")
			const wallet = findEmbeddedWallet(wallets)
			if (error instanceof Error) {
				captureTradeError(error, {
					action: "claim",
					marketAddress: variables.marketAddress,
					walletAddress: wallet?.address,
				})
				console.error("[useClaim]", error)
			}
		},
		onSettled: () => {
			setTimeout(() => setStatus("idle"), 2000)
		},
	})

	const reset = useCallback(() => {
		setStatus("idle")
		mutation.reset()
	}, [mutation])

	return {
		...mutation,
		status,
		reset,
	}
}
